/**
 * OvertimePayrollSettings — Settings → Overtime & Payroll Deadlines
 * (Aug 2026). Three admin-only cards: Overtime Alerts, Payroll Deadlines,
 * Exception Thresholds. Backed by /api/company/overtime-settings and
 * /api/company/payroll-deadlines.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Flame, CalendarClock, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.__BACKEND_URL__}/api`;

const HoursInput = ({ value, onChange, testId, min = 1, max = 48, step = 0.5 }) => (
  <div className="relative w-32">
    <Input type="number" min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(e.target.value)} className="pr-10" data-testid={testId} />
    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs" style={{ color: '#64748B' }}>hrs</span>
  </div>
);

const CheckPill = ({ checked, onToggle, label, testId }) => (
  <button type="button" onClick={onToggle}
    className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
    style={{
      background: checked ? 'rgba(30,136,229,0.2)' : '#1A2433',
      color: checked ? '#60A5FA' : '#64748B',
      border: `1px solid ${checked ? '#1E88E5' : '#2D3748'}`,
    }}
    data-testid={testId}>
    {checked ? '✓ ' : ''}{label}
  </button>
);

export default function OvertimePayrollSettings({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [ot, setOt] = useState({ ot_warning_enabled: true, ot_weekly_threshold: 44, ot_daily_threshold: 8, ot_warning_pct: [75, 90] });
  const [exc, setExc] = useState({ max_shift_hours: 14, break_required_after_hours: 5 });
  const [dl, setDl] = useState({
    payroll_worker_deadline: '', payroll_supervisor_deadline: '',
    payroll_review_deadline: '', payroll_processing_deadline: '',
    payroll_deadline_timezone: 'America/Edmonton', payroll_deadline_notify_hours: [24, 4],
  });
  const [timezones, setTimezones] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [o, d, tz] = await Promise.all([
          axios.get(`${API}/company/overtime-settings`, { headers }),
          axios.get(`${API}/company/payroll-deadlines`, { headers }),
          axios.get(`${API}/timezones`, { headers }),
        ]);
        setOt({
          ot_warning_enabled: o.data.ot_warning_enabled !== false,
          ot_weekly_threshold: o.data.ot_weekly_threshold ?? 44,
          ot_daily_threshold: o.data.ot_daily_threshold ?? 8,
          ot_warning_pct: o.data.ot_warning_pct || [75, 90],
        });
        setExc({
          max_shift_hours: o.data.max_shift_hours ?? 14,
          break_required_after_hours: o.data.break_required_after_hours ?? 5,
        });
        setDl({
          payroll_worker_deadline: d.data.payroll_worker_deadline || '',
          payroll_supervisor_deadline: d.data.payroll_supervisor_deadline || '',
          payroll_review_deadline: d.data.payroll_review_deadline || '',
          payroll_processing_deadline: d.data.payroll_processing_deadline || '',
          payroll_deadline_timezone: d.data.payroll_deadline_timezone || 'America/Edmonton',
          payroll_deadline_notify_hours: d.data.payroll_deadline_notify_hours || [24, 4],
        });
        const list = Array.isArray(tz.data) ? tz.data : tz.data.timezones || [];
        setTimezones(list);
      } catch (e) {
        toast.error('Could not load overtime/payroll settings');
      } finally { setLoading(false); }
    })();
  }, [token]); // eslint-disable-line

  const togglePct = (p) => setOt((s) => ({
    ...s,
    ot_warning_pct: s.ot_warning_pct.includes(p)
      ? s.ot_warning_pct.filter((x) => x !== p)
      : [...s.ot_warning_pct, p].sort((a, b) => a - b),
  }));
  const toggleNotify = (h) => setDl((s) => ({
    ...s,
    payroll_deadline_notify_hours: s.payroll_deadline_notify_hours.includes(h)
      ? s.payroll_deadline_notify_hours.filter((x) => x !== h)
      : [...s.payroll_deadline_notify_hours, h].sort((a, b) => b - a),
  }));

  const saveOt = async () => {
    setSaving('ot');
    try {
      await axios.put(`${API}/company/overtime-settings`, {
        ot_warning_enabled: ot.ot_warning_enabled,
        ot_weekly_threshold: parseFloat(ot.ot_weekly_threshold),
        ot_daily_threshold: parseFloat(ot.ot_daily_threshold),
        ot_warning_pct: ot.ot_warning_pct,
      }, { headers });
      toast.success('Saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save overtime alerts');
    } finally { setSaving(null); }
  };

  const saveExc = async () => {
    setSaving('exc');
    try {
      await axios.put(`${API}/company/overtime-settings`, {
        max_shift_hours: parseFloat(exc.max_shift_hours),
        break_required_after_hours: parseFloat(exc.break_required_after_hours),
      }, { headers });
      toast.success('Saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save exception thresholds');
    } finally { setSaving(null); }
  };

  const saveDl = async () => {
    setSaving('dl');
    try {
      await axios.put(`${API}/company/payroll-deadlines`, {
        payroll_worker_deadline: dl.payroll_worker_deadline || null,
        payroll_supervisor_deadline: dl.payroll_supervisor_deadline || null,
        payroll_review_deadline: dl.payroll_review_deadline || null,
        payroll_processing_deadline: dl.payroll_processing_deadline || null,
        payroll_deadline_timezone: dl.payroll_deadline_timezone,
        payroll_deadline_notify_hours: dl.payroll_deadline_notify_hours,
      }, { headers });
      toast.success('Saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save payroll deadlines');
    } finally { setSaving(null); }
  };

  if (loading) {
    return (
      <Card className="shadow-lg mb-6"><CardContent className="p-6">
        <div className="flex items-center text-sm text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
        </div>
      </CardContent></Card>
    );
  }

  const deadlineFields = [
    ['payroll_worker_deadline', 'Worker submission deadline', 'e.g. Monday 10:00'],
    ['payroll_supervisor_deadline', 'Supervisor approval deadline', 'e.g. Monday 15:00'],
    ['payroll_review_deadline', 'Payroll review deadline', 'e.g. Tuesday 10:00'],
    ['payroll_processing_deadline', 'Payroll processing deadline', 'e.g. Tuesday 12:00'],
  ];

  return (
    <>
      {/* Overtime Alerts */}
      <Card className="shadow-lg mb-6" data-testid="overtime-alerts-card">
        <CardHeader className="bg-[#1A2433] border-b">
          <CardTitle className="text-lg font-bold flex items-center space-x-2">
            <Flame className="w-5 h-5" style={{ color: '#FB923C' }} />
            <span>Overtime Alerts</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-white">Enable overtime warnings</p>
              <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                Warn workers and admins as weekly/daily hours approach the overtime thresholds.
              </p>
            </div>
            <Switch checked={ot.ot_warning_enabled}
              onCheckedChange={(v) => setOt({ ...ot, ot_warning_enabled: v })}
              data-testid="ot-warnings-switch" />
          </div>
          <div className="flex flex-wrap gap-6">
            <div>
              <Label className="text-xs" style={{ color: '#94A3B8' }}>Weekly OT threshold</Label>
              <HoursInput value={ot.ot_weekly_threshold} min={1} max={100}
                onChange={(v) => setOt({ ...ot, ot_weekly_threshold: v })}
                testId="ot-weekly-threshold-input" />
            </div>
            <div>
              <Label className="text-xs" style={{ color: '#94A3B8' }}>Daily OT threshold</Label>
              <HoursInput value={ot.ot_daily_threshold} min={1} max={24}
                onChange={(v) => setOt({ ...ot, ot_daily_threshold: v })}
                testId="ot-daily-threshold-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs" style={{ color: '#94A3B8' }}>Warn at</Label>
            <div className="flex gap-2 mt-1">
              {[75, 90, 100].map((p) => (
                <CheckPill key={p} checked={ot.ot_warning_pct.includes(p)}
                  onToggle={() => togglePct(p)} label={`${p}%`} testId={`ot-warn-pct-${p}`} />
              ))}
            </div>
          </div>
          <Button onClick={saveOt} disabled={saving === 'ot'}
            className="bg-[#1E88E5] hover:bg-blue-600 text-white" data-testid="ot-alerts-save-btn">
            {saving === 'ot' ? 'Saving…' : 'Save Overtime Alerts'}
          </Button>
        </CardContent>
      </Card>

      {/* Payroll Deadlines */}
      <Card className="shadow-lg mb-6" data-testid="payroll-deadlines-card">
        <CardHeader className="bg-[#1A2433] border-b">
          <CardTitle className="text-lg font-bold flex items-center space-x-2">
            <CalendarClock className="w-5 h-5" style={{ color: '#60A5FA' }} />
            <span>Payroll Deadlines</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            {deadlineFields.map(([key, label, ph]) => (
              <div key={key}>
                <Label className="text-xs" style={{ color: '#94A3B8' }}>{label}</Label>
                <Input value={dl[key]} placeholder={ph}
                  onChange={(e) => setDl({ ...dl, [key]: e.target.value })}
                  data-testid={`${key.replaceAll('_', '-')}-input`} />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-6">
            <div>
              <Label className="text-xs" style={{ color: '#94A3B8' }}>Timezone</Label>
              <select value={dl.payroll_deadline_timezone}
                onChange={(e) => setDl({ ...dl, payroll_deadline_timezone: e.target.value })}
                className="block w-64 px-3 py-2 rounded-md text-sm"
                style={{ background: '#1A2433', color: '#fff', border: '1px solid #2D3748' }}
                data-testid="payroll-deadline-timezone-select">
                {!timezones.some((t) => t.value === dl.payroll_deadline_timezone) && (
                  <option value={dl.payroll_deadline_timezone}>{dl.payroll_deadline_timezone}</option>
                )}
                {timezones.map((t) => (
                  <option key={t.value} value={t.value}>{t.label} ({t.value})</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs" style={{ color: '#94A3B8' }}>Notify before</Label>
              <div className="flex gap-2 mt-1">
                {[24, 4].map((h) => (
                  <CheckPill key={h} checked={dl.payroll_deadline_notify_hours.includes(h)}
                    onToggle={() => toggleNotify(h)} label={`${h}h`} testId={`notify-before-${h}h`} />
                ))}
              </div>
            </div>
          </div>
          <p className="text-xs" style={{ color: '#64748B' }}>
            Format: weekday + 24-hour time, e.g. "Monday 10:00". Leave a field blank to disable that deadline.
          </p>
          <Button onClick={saveDl} disabled={saving === 'dl'}
            className="bg-[#1E88E5] hover:bg-blue-600 text-white" data-testid="payroll-deadlines-save-btn">
            {saving === 'dl' ? 'Saving…' : 'Save Payroll Deadlines'}
          </Button>
        </CardContent>
      </Card>

      {/* Exception Thresholds */}
      <Card className="shadow-lg mb-6" data-testid="exception-thresholds-card">
        <CardHeader className="bg-[#1A2433] border-b">
          <CardTitle className="text-lg font-bold flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5" style={{ color: '#FBBF24' }} />
            <span>Exception Thresholds</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-wrap gap-6">
            <div>
              <Label className="text-xs" style={{ color: '#94A3B8' }}>Max shift length before exception</Label>
              <HoursInput value={exc.max_shift_hours} min={1} max={48}
                onChange={(v) => setExc({ ...exc, max_shift_hours: v })}
                testId="max-shift-hours-input" />
            </div>
            <div>
              <Label className="text-xs" style={{ color: '#94A3B8' }}>Break required after</Label>
              <HoursInput value={exc.break_required_after_hours} min={0.5} max={24}
                onChange={(v) => setExc({ ...exc, break_required_after_hours: v })}
                testId="break-required-after-input" />
            </div>
          </div>
          <p className="text-xs" style={{ color: '#64748B' }}>
            Shifts longer than the max, or without a break past the break threshold, are flagged as timesheet exceptions.
          </p>
          <Button onClick={saveExc} disabled={saving === 'exc'}
            className="bg-[#1E88E5] hover:bg-blue-600 text-white" data-testid="exception-thresholds-save-btn">
            {saving === 'exc' ? 'Saving…' : 'Save Exception Thresholds'}
          </Button>
        </CardContent>
      </Card>
    </>
  );
}
